import React, { useState, useEffect } from 'react';
import { Box, Text, HStack, VStack, Button } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import EditButton from './EditButton';
import PhotoGallery from './PhotoGallery';
import { useConfig } from '../contexts/ConfigContext';
import { useUser } from '../contexts/UserContext';
import { useSupabase } from '../contexts/SupabaseContext';
import { ROUTES } from '../constants';
import { getCachedUserProfile, isSubscriptionExpired } from '../utils';

interface InfluencerMyProfileProps {
  onDialogStateChange?: (isOpen: boolean) => void;
}

const InfluencerMyProfile: React.FC<InfluencerMyProfileProps> = ({ onDialogStateChange }) => {
  const navigate = useNavigate();
  const { config } = useConfig();
  const { user, logout: logoutUser } = useUser();
  const { logout: logoutSupabase } = useSupabase();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileImages, setProfileImages] = useState<string[]>([]);
  const [upcomingEventsCount] = useState(0);
  const [myEventsCount] = useState(0);
  const [paidTillDate, setPaidTillDate] = useState<string | null>(null);
  
  // Fetch profile data on mount
  useEffect(() => {
    const fetchProfile = async () => {
      if (!config?.apiUrl || !user?.id) {
        // Use user context name as fallback
        if (user?.first_name && user?.last_name) {
          setProfileName(`${user.first_name} ${user.last_name}`);
        }
        return;
      }

      try {
        const profileData = await getCachedUserProfile(config.apiUrl, user.id, user.userType);

        // Set name from profile data
        if (profileData.data?.firstName && profileData.data?.lastName) {
          setProfileName(`${profileData.data.firstName} ${profileData.data.lastName}`);
        } else if (user?.first_name && user?.last_name) {
          setProfileName(`${user.first_name} ${user.last_name}`);
        }

        // Set profile images if available
        if (profileData.data?.photos && Array.isArray(profileData.data.photos) && profileData.data.photos.length > 0) {
          setProfileImages(profileData.data.photos);
        } else {
          // No images available
          setProfileImages([]);
        }

        // Set subscription status
        setPaidTillDate(profileData.data?.paidTillDate || null);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        // Use user context name as fallback
        if (user?.first_name && user?.last_name) {
          setProfileName(`${user.first_name} ${user.last_name}`);
        }
        // No images available on error
        setProfileImages([]);
      }
    };

    fetchProfile();
  }, [config, user]);

  const handleLogout = async () => {
    try {
      // Clear user context
      logoutUser();
      
      // Clear any stored return paths
      sessionStorage.removeItem('authReturnPath');
      
      // Logout from Supabase
      await logoutSupabase();
      // Navigate to landing page
      setShowLogoutDialog(false);
      onDialogStateChange?.(false);
      navigate(ROUTES.HOME);
    } catch (error: any) {
      console.error('Logout error:', error);
      
      // Ensure everything is cleared even on error
      logoutUser();
      sessionStorage.removeItem('authReturnPath');
      setShowLogoutDialog(false);
      onDialogStateChange?.(false);
      
      // Navigate to landing page
      navigate(ROUTES.HOME);
    }
  };

  const handleCancelLogout = () => {
    setShowLogoutDialog(false);
    onDialogStateChange?.(false);
  };

  const handleShowLogoutDialog = () => {
    setShowLogoutDialog(true);
    onDialogStateChange?.(true);
  };

  const handleEditProfile = () => {
    navigate(ROUTES.EDIT_PROFILE);
  };

  return (
    <Box
      position="absolute"
      top="0"
      left="0"
      right="0"
      bottom="0"
      zIndex={2}
    >
      {/* Profile Background Image */}
      <PhotoGallery
        images={profileImages}
      />

      {/* Profile Header */}
      <Box
        position="absolute"
        top="44px"
        left="0"
        right="0"
        height="60px"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        paddingLeft="24px"
        paddingRight="24px"
        zIndex={3}
      >
        <Text className="title-text" >
          My profile
        </Text>
        <EditButton
          onClick={handleEditProfile}
          borderColor="#FFFFFF"
          hoverBorderColor="#FFFFFF"
          hoverBackgroundColor="rgba(28, 28, 30, 0.9)"
          backgroundColor="rgba(28, 28, 30, 0.8)"
        />
      </Box>

      {/* Profile Avatar and Name */}
      <Box
        position="absolute"
        top="388px"
        left="24px"
        zIndex={3}
      >
        <Text className="title-text-big" >
          {profileName || 'Loading...'}
        </Text>              
      </Box>

      {/* Divider */}
      <Box 
        className="navigation-bar-divider"
        top="437px"
      />

      {/* Subscription Status */}
      <Box
        position="absolute"
        top="610px"
        left="24px"
        right="24px"
        zIndex={3}        
      >
        <HStack spacing="8px">
          <Box
            width="8px"
            height="8px"
            borderRadius="50%"
            backgroundColor={isSubscriptionExpired(paidTillDate) ? "#595C5D" : "#C6D959"}
          />
          {isSubscriptionExpired(paidTillDate) ? (
            <Text className="subtitle-text" color="#595C5D">
              Subscription expired
            </Text>
          ) : paidTillDate ? (
            <Text className="subtitle-text" color="#C6D959">
              Subscription valid until {new Date(paidTillDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </Text>
          ) : (
            <Text className="subtitle-text" color="#595C5D">
              No active subscription
            </Text>
          )}
        </HStack>
      </Box>

      {/* Event Statistics Cards */}
      <Box
        position="absolute"
        top="462px"
        left="24px"
        right="24px"
        zIndex={1}
      >
        <VStack spacing="10px" align="stretch">
          {/* Upcoming Events Card */}
          <Box
            width="100%"
            height="56px"
            backgroundColor="#120F1B"
            border="1px solid"
            borderColor="linear-gradient(134deg, rgba(29, 27, 38, 1) 0%, rgba(107, 99, 140, 1) 50%, rgba(29, 27, 38, 1) 100%)"
            borderRadius="20px"
            display="flex"
            alignItems="center"
            padding="0 20px"
            boxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
          >
            <Box
              width="0px"
              height="24px"
              borderLeft="1px solid"
              borderColor="linear-gradient(134deg, rgba(29, 27, 38, 1) 0%, rgba(107, 99, 140, 1) 50%, rgba(29, 27, 38, 1) 100%)"
              marginRight="20px"
            />
            <Text
              className="title-text"
              marginRight="16px"
            >
              {upcomingEventsCount}
            </Text>
            <Text className="subtitle-text" >
              Upcoming Events
            </Text>
          </Box>

          {/* My Events Card */}
          <Box
            width="100%"
            height="56px"
            backgroundColor="#120F1B"
            border="1px solid"
            borderColor="linear-gradient(134deg, rgba(29, 27, 38, 1) 0%, rgba(107, 99, 140, 1) 50%, rgba(29, 27, 38, 1) 100%)"
            borderRadius="20px"
            display="flex"
            alignItems="center"
            padding="0 20px"
            boxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
          >
            <Box
              width="0px"
              height="24px"
              borderLeft="1px solid"
              borderColor="linear-gradient(134deg, rgba(29, 27, 38, 1) 0%, rgba(107, 99, 140, 1) 50%, rgba(29, 27, 38, 1) 100%)"
              marginRight="20px"
            />
            <Text
              className="title-text"
              marginRight="16px"
            >
              {myEventsCount}
            </Text>
            <Text className="subtitle-text" >
              My events
            </Text>
          </Box>
        </VStack>
      </Box>

      {/* Logout Section */}
      <Box
        position="absolute"
        top="670px"
        left="24px"
        zIndex={3}
      >
        <HStack spacing="6px" alignItems="center" cursor="pointer" onClick={handleShowLogoutDialog}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 0C6.55 0 7 0.45 7 1V3H14C14.55 3 15 3.45 15 4V12C15 12.55 14.55 13 14 13H7V15C7 15.55 6.55 16 6 16H1C0.45 16 0 15.55 0 15V1C0 0.45 0.45 0 1 0H6ZM8 5V11H13V5H8ZM2 2V14H5V2H2Z"
              fill="#595C5D"
            />
          </svg>
          <Text
            className="regular-text"
            color="#595C5D"
          >
            Log out
          </Text>
        </HStack>
      </Box>

      {/* Logout Dialog */}
      {showLogoutDialog && (
        <>
          {/* Modal Backdrop */}
          <Box className="modal-backdrop" zIndex={1} onClick={handleCancelLogout} />

          {/* Dialog */}
          <Box className="bottom-sheet-dialog" height="360px" zIndex={3} onClick={(e) => e.stopPropagation()}>
            {/* Background Image */}
            <Box
              className="bottom-sheet-bg"
              style={{ backgroundImage: "url('/assets/images/confirmation-sheet-bg.svg')" }}
            />

            {/* Bottom Sheet Indicator */}
            <Box
              className="bottom-sheet-indicator"
              style={{ backgroundImage: "url('/assets/images/bottom-sheet-indicator.svg')" }}
            />

            {/* Content */}
            <VStack className="bottom-sheet-content">
              {/* Main Question */}
              <Text className="dialog-title">
                Are you sure?
              </Text>

              {/* Subtitle */}
              <Text className="dialog-subtitle">
                you want to log out from the app
              </Text>

              {/* Cancel Button */}
              <Button className="button-cancel" onClick={handleCancelLogout}>
                Cancel
              </Button>

              {/* Logout Button */}
              <Button className="button-confirm" onClick={handleLogout}>
                Log out
              </Button>
            </VStack>

            <Box
              className="bottom-sheet-footer"
              style={{ backgroundImage: "url('/assets/images/home-indicator-confirmation.svg')" }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default InfluencerMyProfile;
